import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import styles from "./ReportsDetailTable.module.scss";
import { SearchIcon } from "customIcons";
import { formatDate, formatTime } from "@utils/common";

// ============================================================================
// Types & Interfaces
// ============================================================================
interface ReportsDetailTableProps {
  data?: any[];
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
}

const ReportsDetailTable: React.FC<ReportsDetailTableProps> = ({
  data,
  searchTerm,
  onSearchChange,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "in_progress":
        return "#F70";
      case "Completed":
        return "#65CF21";
      case "Failed":
        return "#E74848";
      default:
        return "#0E0F0C";
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className={`${styles["reports-table"]} reports-table`}>
      {/* Action Bar */}
      <div className={styles["reports-table__action-bar"]}>
        <div className={styles["reports-table__action-bar__left"]}></div>
        <div className={styles["reports-table__action-bar__right"]}>
          {/* Search Bar */}
          <div className={styles["reports-table__action-bar__right__search"]}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by Email/Name/Phone Number"
              className={
                styles["reports-table__search-bar-container__inner-input"]
              }
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className={styles["reports-table__table-wrapper"]}>
        <TableContainer>
          <Table className={styles["reports-table__table"]}>
            {/* Table Header */}
            <TableHead>
              <TableRow className={styles["reports-table__head-row"]}>
                <TableCell>Credit ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Mobile Number</TableCell>
                <TableCell>Company Name</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expiration Date/Time(UAE)</TableCell>
                <TableCell>Email Status</TableCell>
                <TableCell>Email Action Date/Time(UAE)</TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body */}
            <TableBody>
              {data?.map((report, index) => (
                <TableRow
                  key={index}
                  className={styles["reports-table__body-row"]}
                  sx={{ height: "84px" }}
                >
                  {/* Credit ID */}
                  <TableCell>{report?.id}</TableCell>

                  {/* Name & Email */}
                  <TableCell>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>{report?.name}</span>
                      <span style={{ color: "#868785", fontWeight: "500" }}>
                        {report?.customer_email}
                      </span>
                    </div>
                  </TableCell>

                  {/* Mobile Number */}
                  <TableCell>{report?.customer_phone}</TableCell>

                  {/* Company Name */}
                  <TableCell>{report?.company_name}</TableCell>

                  {/* Credit Value */}
                  <TableCell>{report?.credit_value}</TableCell>

                  {/* Status */}
                  <TableCell
                    style={{ color: getStatusColor(report?.credit_status) }}
                  >
                    {report?.credit_status}
                  </TableCell>

                  {/* Date & Time */}
                  <TableCell>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>
                      {formatDate(report?.expiration_date)}
                      </span>
                      <span style={{ color: "#868785", fontWeight: "500" }}>
                        {formatTime(report?.expiration_date, "Asia/Dubai", {
                          ampm: false,
                        })}{" "}
                        UAE
                      </span>
                    </div>
                  </TableCell>
                  {/* Email Status */}
                  <TableCell>{report?.email_status}</TableCell>

                  {/* Email Action Date & Time */}
                  <TableCell>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>{formatDate(report?.email_action_date)}</span>
                      <span style={{ color: "#868785", fontWeight: "500" }}>
                        {formatTime(report?.email_action_date, "Asia/Dubai", {
                          ampm: false,
                        })}{" "}
                        UAE
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default ReportsDetailTable;
